import { emitMeaningfulAction, emitReviewMoment } from '@/lib/meaningfulActions';
import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CalendarHeart } from 'lucide-react';
import { useWeeklyReview, formatWeekRange, type WeeklyReview } from '@/hooks/useWeeklyReview';
import WeeklyReviewCards from './WeeklyReviewCards';
import { track } from '@/lib/analytics';
import { useCommunityTourDeferred } from '@/components/onboarding/CommunityTourContext';

/**
 * Only one recap dialog may be open at a time. A recap opened deliberately
 * (e.g. from Profile) suppresses the app-wide auto-surfacing instance, so the
 * two Radix dialogs never stack and steal each other's pointer events.
 */
let openCount = 0;
const openListeners = new Set<() => void>();
const emitOpenChange = () => openListeners.forEach(l => l());
const subscribeOpen = (listener: () => void) => {
  openListeners.add(listener);
  return () => void openListeners.delete(listener);
};
const getOpenCount = () => openCount;

interface WeeklyReviewModalProps {
  /** Externally controlled review (profile / history entry points). */
  review?: WeeklyReview | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  source?: 'prompt' | 'profile' | 'history';
  /** Skip the intro card and go straight to the recap cards. */
  skipIntro?: boolean;
}

/**
 * Entry card for the weekly recap. With no props it auto-surfaces the user's
 * pending recap; with props it renders a specific recap on demand.
 */
const WeeklyReviewModal: React.FC<WeeklyReviewModalProps> = ({
  review: controlledReview,
  open: controlledOpen,
  onOpenChange,
  source = 'prompt',
  skipIntro = false,
}) => {
  const navigate = useNavigate();
  const tourDeferred = useCommunityTourDeferred();
  const { pending, markViewed, markDismissed } = useWeeklyReview();
  const [autoOpen, setAutoOpen] = useState(false);
  const [started, setStarted] = useState(false);
  // Keep the auto-surfaced recap on screen after it is marked viewed (it then
  // stops being "pending", which previously unmounted the dialog instantly).
  const [heldReview, setHeldReview] = useState<WeeklyReview | null>(null);
  useEffect(() => {
    if (pending) setHeldReview(pending);
  }, [pending]);

  const isControlled = controlledOpen !== undefined;
  const review = isControlled ? controlledReview ?? null : (autoOpen ? heldReview ?? pending : pending);
  const controlledIsOpen = isControlled && !!controlledOpen;
  const otherOpen = useSyncExternalStore(subscribeOpen, getOpenCount, getOpenCount) > 0;
  const open = isControlled ? controlledIsOpen : autoOpen && !otherOpen;

  // Only deliberately-opened (controlled) recaps register, so the app-wide
  // auto instance stands down while one is open.
  useEffect(() => {
    if (!isControlled || !controlledIsOpen) return;
    openCount += 1;
    emitOpenChange();
    return () => {
      openCount = Math.max(0, openCount - 1);
      emitOpenChange();
    };
  }, [isControlled, controlledIsOpen]);

  useEffect(() => {
    if (isControlled || !pending || tourDeferred) return;
    setAutoOpen(true);
    track('Weekly Review Available', {
      week_start: pending.week_start,
      week_end: pending.week_end,
      badges_earned: pending.badges_earned,
    });
  }, [isControlled, pending, tourDeferred]);

  // Deliberately-opened recaps go straight to the cards and count as viewed.
  useEffect(() => {
    if (!controlledIsOpen || !skipIntro || !controlledReview) return;
    setStarted(true);
    void markViewed(controlledReview.id);
    track('Weekly Review Opened', { week_start: controlledReview.week_start, source });
  }, [controlledIsOpen, skipIntro, controlledReview, markViewed, source]);

  const setOpen = (value: boolean) => {
    if (isControlled) onOpenChange?.(value);
    else setAutoOpen(value);
    if (!value) setStarted(false);
  };

  const handleClose = (value: boolean) => {
    if (!value && review && !started && source === 'prompt') {
      track('Weekly Review Dismissed', { week_start: review.week_start, card_index: 0 });
      void markDismissed(review.id);
    }
    setOpen(value);
  };

  if (!review) return null;

  const start = () => {
    setStarted(true);
    void markViewed(review.id);
    track('Weekly Review Opened', { week_start: review.week_start, source });
  };

  const finish = () => {
    emitReviewMoment('weekly_review');
    void markDismissed(review.id);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        {!started ? (
          <>
            <DialogHeader className="items-center text-center">
              <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 border border-primary/30">
                <CalendarHeart className="h-7 w-7 text-primary" aria-hidden="true" />
              </div>
              <DialogTitle>Your Week in Review</DialogTitle>
              <DialogDescription>{formatWeekRange(review.week_start, review.week_end)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 pt-2">
              <Button className="w-full" onClick={start}>
                View My Week
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => handleClose(false)}>
                Maybe later
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>Your Week in Review</DialogTitle>
              <DialogDescription>A recap of your week on Vent</DialogDescription>
            </DialogHeader>
            <WeeklyReviewCards
              review={review}
              onFinish={finish}
              onViewBadges={() => {
                finish();
                navigate('/app/profile#badges');
              }}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WeeklyReviewModal;
